import { RadioGroup, Radio, RadioGroupProps } from '@nextui-org/react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface RadioOption {
    value: string | boolean
    label: string
}

interface RadioGroupControlProps<T extends FieldValues>
    extends Omit<RadioGroupProps, 'onChange'> {
    name: Path<T>
    control: Control<T>
    rules?: Record<string, any>
    options: RadioOption[]
    label?: string
    onChangeCustom?: (value: string | boolean) => void
    orientation?: 'horizontal' | 'vertical'
}

export function RadioGroupControl<T extends FieldValues>({
    name,
    control,
    rules,
    options,
    label,
    onChangeCustom,
    orientation = 'horizontal',
    ...props
}: RadioGroupControlProps<T>): React.ReactElement {
    const { t } = useTranslation()

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => (
                <div className="w-full">
                    <RadioGroup
                        label={label ? t(label) : ''}
                        orientation={orientation}
                        {...props}
                        value={field.value?.toString() || ''}
                        onChange={(e) => {
                            const value = e.target.value
                            // Keep as string to match API format

                            field.onChange(value)
                            if (onChangeCustom) {
                                onChangeCustom(value)
                            }
                        }}
                        isInvalid={error ? true : false}
                        errorMessage={error ? `${error.message}*` : ''}
                    >
                        {options.map((option) => (
                            <Radio
                                key={option.value.toString()}
                                value={option.value.toString()}
                                className="w-full"
                            >
                                {option.label}
                            </Radio>
                        ))}
                    </RadioGroup>
                </div>
            )}
        />
    )
}
